import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { pricingApi } from '../services/api';

const CaseysPricing = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);
  const [stationEdits, setStationEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadBatches = useCallback(async () => {
    try {
      const data = await pricingApi.listCaseysBatches();
      setBatches(data.batches || []);
      setBatchId((prev) => prev || (data.batches?.[0]?._id ?? ''));
    } catch (e) {
      setError(e.message || 'Failed to load batches');
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const loadRows = useCallback(async () => {
    if (!batchId) {
      setRows([]);
      setTotalRows(0);
      return;
    }
    setLoadingRows(true);
    setError(null);
    try {
      const data = await pricingApi.listBatchRows(batchId, { limit: 500 });
      setRows(data.rows || []);
      setTotalRows(data.total ?? (data.rows || []).length);
      const next = {};
      (data.rows || []).forEach((r) => {
        next[r._id] = r.stationCode || '';
      });
      setStationEdits(next);
    } catch (e) {
      setError(e.message || 'Failed to load rows');
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  }, [batchId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleUpload = async () => {
    if (!file) {
      setError('Choose a .csv file first');
      return;
    }
    setUploading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await pricingApi.uploadCaseysCsv(file);
      setMessage(
        `Imported ${result.rowCount} rows into batch ${result.batchId}.` +
          (result.warnings?.length ? ` Warnings: ${result.warnings.join('; ')}` : '')
      );
      setFile(null);
      await loadBatches();
      setBatchId(result.batchId);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveStation = async (rowId) => {
    const code = (stationEdits[rowId] || '').trim();
    if (!code) {
      setError('Enter a QuikQ stationCode before saving');
      return;
    }
    setSavingId(rowId);
    setError(null);
    try {
      const { row } = await pricingApi.mapStagingRowStation(rowId, code);
      setStationEdits((prev) => ({ ...prev, [rowId]: row.stationCode || '' }));
      setRows((prev) =>
        prev.map((r) => (r._id === rowId ? { ...r, stationCode: row.stationCode, mappedAt: row.mappedAt } : r))
      );
    } catch (e) {
      setError(e.message || 'Failed to save mapping');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Casey&apos;s pricing (CSV staging)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload a Casey's Cost Plus CSV. Rows are stored in MongoDB (<code>admin_dashboard</code>) for review.
        Map each Casey's site # to a QuikQ <code>stationCode</code>; mappings persist for future imports.
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload CSV
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <Button variant="outlined" component="label">
            Select file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : 'No file selected'}</Typography>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? <CircularProgress size={22} /> : 'Upload & stage'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 320 }}>
            <InputLabel id="batch-label">Import batch</InputLabel>
            <Select
              labelId="batch-label"
              label="Import batch"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
            >
              {batches.map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.fileName} — {b.rowCount} rows — {new Date(b.uploadedAt).toLocaleString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={loadBatches}>Refresh batches</Button>
          <Button onClick={loadRows} disabled={!batchId || loadingRows}>
            Refresh rows
          </Button>
        </Box>
        {batchId && batches.length > 0 && (
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Total rows in batch: {totalRows}
          </Typography>
        )}
      </Paper>

      <TableContainer component={Paper}>
        {loadingRows ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Site #</TableCell>
                <TableCell>City</TableCell>
                <TableCell>ST</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Retail</TableCell>
                <TableCell align="right">Fleet</TableCell>
                <TableCell>QuikQ stationCode</TableCell>
                <TableCell width={120} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>{r.caseysSiteNumber}</TableCell>
                  <TableCell>{r.city}</TableCell>
                  <TableCell>{r.state}</TableCell>
                  <TableCell>{r.product}</TableCell>
                  <TableCell align="right">{r.retailPrice ?? '—'}</TableCell>
                  <TableCell align="right">{r.fleetPrice ?? '—'}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={stationEdits[r._id] ?? ''}
                      onChange={(e) =>
                        setStationEdits((prev) => ({ ...prev, [r._id]: e.target.value }))
                      }
                      placeholder="stationCode"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => saveStation(r._id)}
                      disabled={savingId === r._id}
                    >
                      {savingId === r._id ? '…' : 'Save'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default CaseysPricing;
